import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiX,
  FiCreditCard,
  FiRepeat,
  FiFileText,
  FiExternalLink,
  FiCheck,
  FiZap,
} from "react-icons/fi";

import {
  checkoutOffer,
  checkoutPlan,
  formatBRL,
  getErrorMessage,
  intervalLabel,
} from "../../services/billingApi";
import useMercadoPago from "../../hooks/useMercadoPago";
import CardForm from "./CardForm";
import PixPanel from "./PixPanel";
import "./Checkout.css";

const PixIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2 22 12 12 22 2 12z" strokeLinejoin="round" />
  </svg>
);

/**
 * Métodos disponíveis por tipo de item.
 *
 * `CARD_RECURRING` só aparece em cobranças com ciclo (mensal/anual): é a
 * assinatura de verdade, com débito automático no cartão.
 */
const buildMethods = ({ isRecurring, maxInstallments }) => {
  const methods = [
    {
      id: "PIX",
      label: "PIX",
      icon: PixIcon,
      hint: isRecurring
        ? "Aprovação na hora. Renovação manual a cada ciclo."
        : "Aprovação em segundos.",
      highlight: true,
    },
  ];

  if (isRecurring) {
    methods.push({
      id: "CARD_RECURRING",
      label: "Cartão (renovação automática)",
      icon: FiRepeat,
      hint: "Cobrado automaticamente a cada ciclo. Cancele quando quiser.",
      highlight: true,
    });
  }

  methods.push({
    id: "CARD",
    label: isRecurring ? "Cartão (pagamento único)" : "Cartão de crédito",
    icon: FiCreditCard,
    hint:
      maxInstallments > 1
        ? `Parcele em até ${maxInstallments}x.`
        : "Cobrança única, sem renovação automática.",
  });

  if (!isRecurring) {
    methods.push({
      id: "BOLETO",
      label: "Boleto bancário",
      icon: FiFileText,
      hint: "Compensa em até 3 dias úteis.",
    });
  }

  methods.push({
    id: "CHECKOUT_PRO",
    label: "Outras formas (Mercado Pago)",
    icon: FiExternalLink,
    hint: "Saldo em conta, carteira digital e outros meios.",
  });

  return methods;
};

/**
 * Modal de checkout.
 *
 * @param {object} item          Plano ou oferta (serializado pelo backend).
 * @param {"PLAN"|"OFFER"} kind  Define qual endpoint de checkout usar.
 * @param {object} metadata      Campos obrigatórios da oferta (productId etc.).
 */
const CheckoutModal = ({ isOpen, onClose, item, kind = "PLAN", publicKey, metadata = {}, onSuccess }) => {
  const [method, setMethod] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const mercadoPago = useMercadoPago(publicKey);

  const isRecurring = useMemo(() => {
    if (!item) return false;
    if (kind === "PLAN") return item.interval === "MONTH" || item.interval === "YEAR";
    return item.billingType === "RECURRING";
  }, [item, kind]);

  const amountCents = useMemo(() => {
    if (!item) return 0;
    return kind === "PLAN" ? item.priceCents : item.defaultPriceCents;
  }, [item, kind]);

  const maxInstallments = item?.maxInstallments || 1;
  const methods = useMemo(
    () => buildMethods({ isRecurring, maxInstallments }),
    [isRecurring, maxInstallments]
  );

  // Reinicia o fluxo sempre que o modal reabre com outro item.
  useEffect(() => {
    if (!isOpen) return;
    setMethod(null);
    setCheckout(null);
    setError(null);
    setDone(false);
    setSubmitting(false);
  }, [isOpen, item?.code]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const runCheckout = useCallback(
    async (selectedMethod, payload = {}) => {
      setSubmitting(true);
      setError(null);

      try {
        const request =
          kind === "PLAN"
            ? checkoutPlan({ planCode: item.code, method: selectedMethod, ...payload })
            : checkoutOffer({
                offerCode: item.code,
                method: selectedMethod,
                metadata,
                ...payload,
              });

        const result = await request;
        setCheckout(result);

        // Checkout Pro e preapproval sem token levam o usuário para o Mercado Pago.
        if (result?.initPoint && ["CHECKOUT_PRO", "CARD_RECURRING"].includes(selectedMethod)) {
          window.location.href = result.initPoint;
          return;
        }

        const approved =
          result?.status === "approved" ||
          result?.status === "authorized" ||
          result?.order?.status === "PAID";

        if (approved) {
          setDone(true);
          onSuccess?.(result);
        }

        if (result?.status === "rejected") {
          setError(
            "Pagamento recusado pelo emissor do cartão. Tente outro cartão ou pague via PIX."
          );
        }
      } catch (requestError) {
        setError(getErrorMessage(requestError, "Não foi possível iniciar o pagamento."));
      } finally {
        setSubmitting(false);
      }
    },
    [item, kind, metadata, onSuccess]
  );

  const handleSelectMethod = (selected) => {
    setMethod(selected);
    setError(null);

    // Métodos sem formulário disparam a chamada imediatamente.
    if (["PIX", "BOLETO", "CHECKOUT_PRO"].includes(selected)) {
      runCheckout(selected);
    }
  };

  const handlePixPaid = useCallback(
    (status) => {
      setDone(true);
      onSuccess?.(status);
    },
    [onSuccess]
  );

  if (!isOpen || !item) return null;

  const priceLabel =
    kind === "PLAN"
      ? `${formatBRL(amountCents)}${intervalLabel(item.interval)}`
      : `${formatBRL(amountCents)}${item.billingType === "RECURRING" ? "/mês" : ""}`;

  return createPortal(
    <div className="checkout-overlay" role="dialog" aria-modal="true">
      <div className="checkout-modal">
        <header className="checkout-modal__header">
          <div>
            <p className="checkout-modal__eyebrow">
              {kind === "PLAN" ? "Assinatura" : "Contratação"}
            </p>
            <h3>{item.name}</h3>
            <p className="checkout-modal__price">{priceLabel}</p>
          </div>
          <button type="button" className="checkout-modal__close" onClick={onClose} aria-label="Fechar">
            <FiX />
          </button>
        </header>

        <div className="checkout-modal__body">
          {done ? (
            <div className="checkout-done">
              <div className="checkout-done__icon">
                <FiCheck />
              </div>
              <h4>Tudo certo!</h4>
              <p className="checkout__muted">
                {kind === "PLAN"
                  ? `Seu ${item.name} já está ativo.`
                  : `${item.name} foi liberado na sua conta.`}
              </p>
              <button type="button" className="button button--primary checkout__submit" onClick={onClose}>
                Voltar ao painel
              </button>
            </div>
          ) : (
            <>
              {!method && (
                <div className="checkout-methods">
                  <p className="checkout-methods__title">Como você prefere pagar?</p>
                  {methods.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={`checkout-method${option.highlight ? " checkout-method--highlight" : ""}`}
                        onClick={() => handleSelectMethod(option.id)}
                        disabled={submitting}
                      >
                        <span className="checkout-method__icon">
                          <Icon />
                        </span>
                        <span className="checkout-method__text">
                          <strong>{option.label}</strong>
                          <small>{option.hint}</small>
                        </span>
                        {option.highlight && (
                          <span className="checkout-method__tag">
                            <FiZap /> recomendado
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {method && !checkout && submitting && (
                <p className="checkout__muted checkout__loading">Preparando seu pagamento...</p>
              )}

              {method === "PIX" && checkout?.qrCode && (
                <PixPanel checkout={checkout} onPaid={handlePixPaid} />
              )}

              {method === "BOLETO" && checkout?.ticketUrl && (
                <div className="checkout-boleto">
                  <p className="checkout__muted">
                    Seu boleto foi gerado. A liberação acontece após a compensação
                    (até 3 dias úteis).
                  </p>
                  <a
                    className="button button--primary checkout__submit"
                    href={checkout.ticketUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FiFileText /> Abrir boleto
                  </a>
                </div>
              )}

              {(method === "CARD" || method === "CARD_RECURRING") && (
                <CardForm
                  amountCents={amountCents}
                  maxInstallments={method === "CARD" ? maxInstallments : 1}
                  mercadoPago={mercadoPago}
                  submitting={submitting}
                  submitLabel={method === "CARD_RECURRING" ? "Assinar" : "Pagar agora"}
                  recurringNotice={
                    method === "CARD_RECURRING"
                      ? "Cobrança automática a cada ciclo. Você pode cancelar a qualquer momento pelo painel."
                      : null
                  }
                  onSubmit={(payload) => runCheckout(method, payload)}
                />
              )}

              {error && <p className="checkout__error">{error}</p>}

              {method && !done && (
                <button
                  type="button"
                  className="checkout__back"
                  onClick={() => {
                    setMethod(null);
                    setCheckout(null);
                    setError(null);
                  }}
                >
                  ← Escolher outra forma de pagamento
                </button>
              )}
            </>
          )}
        </div>

        <footer className="checkout-modal__footer">
          <span>Pagamento processado por</span>
          <strong>Mercado Pago</strong>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default CheckoutModal;
